<!--
*  TTTech nerve-ui-components
*  Copyright(c) 2021. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <v-dialog
    id="iiotVmVideoOptionDialog"
    v-model="dialogState"
    data-cy="iiotVmVideoOptionDialog"
    class="dialog_scroll"
    max-width="1000px"
    max-height="1300px"
  >
    <v-card>
      <v-col id="workload-control" xs12 md11 class="page-wrapper-dialog">
        <v-layout class="vm-title" wrap column>
          <h1 id="iiotVmVideoOptionsTitle" data-cy="iiotVmVideoOptionsTitle">
            {{ $t('workloadManagement.vmVideoOptionTitle') }}
          </h1>
          <v-divider />
        </v-layout>
        <v-list class="list-style">
          <v-subheader>
            <v-row class="row-height-header">
              <v-col cols="12" md="4" sm="4">
                <h3 class="text-style">
                  {{ $t('workloadManagement.displayOption') }}
                </h3>
              </v-col>
              <v-col cols="12" md="4" sm="4">
                <h3 class="text-style">
                  {{ $t('workloadManagement.virtualMachineName') }}
                </h3>
              </v-col>
              <v-col cols="12" md="4" sm="4">
                <h3 class="text-style">
                  {{ $t('workloadManagement.displaySequence') }}
                </h3>
              </v-col>
            </v-row>
          </v-subheader>
          <v-list-item v-for="item in array" :key="item.name">
            <v-row align="center" justify="center" class="row-height">
              <v-col sm="4">
                <v-simple-checkbox
                  :id="`checkbox_${item.deviceId}`"
                  v-model="item.display"
                  :disabled="isDisabled(item)"
                  class="checkbox_display"
                  @click="enablingCheckbox(item)"
                />
              </v-col>

              <v-col sm="4">
                <p class="name-style">{{ item.name }}</p>
              </v-col>
              <v-col sm="4">
                <div v-if="item.display" class="item_display">
                  <v-row>
                    <v-col col="12" md="5" sm="8">
                      <span id="itemSequence" class="sequence-style">{{ item.sequence }}</span>
                    </v-col>
                    <v-col :id="`increment_${item.sequence}`" col="12" md="1" sm="1">
                      <v-icon v-if="!isElementOnBottomOfTheList(item)" @click="increment(item)">
                        mdi-chevron-down
                      </v-icon>
                    </v-col>
                    <v-col :id="`decrement_${item.sequence}`" col="12" md="1" sm="1">
                      <v-icon v-if="!isElementOnTopOfTheList(item)" @click="decrement(item)"> mdi-chevron-up </v-icon>
                    </v-col>
                  </v-row>
                  <v-text-field v-if="item.display === false" readonly type="number" class="number-text-field" />
                </div>
              </v-col>
              <v-divider class="divider-width mt-n5" />
            </v-row>
          </v-list-item>
        </v-list>
        <v-card-text>
          <div class="v-card-margin-left">
            <p>{{ videoDisplayModeMessage() }}</p>
          </div>
        </v-card-text>
        <v-card-actions class="right-icon">
          <v-spacer />
          <nerve-button
            id="iiotVmVideoOptionsCancelBtn"
            :text="$t('baseForm.cancelBtn')"
            type-of-btn="close"
            size="normal"
            class="ml-7"
            data-cy="iiotVmVideoOptionsCancelBtn"
            @click-event="close()"
          />

          <nerve-button
            id="iiotVmVideoOptionsSaveBtn"
            :text="$t('baseForm.saveBtn')"
            type-of-btn="action"
            data-cy="iiotVmVideoOptionsSaveBtn"
            size="normal"
            class="ml-7"
            type="submit"
            @click-event="save()"
          />
        </v-card-actions>
      </v-col>
    </v-card>
  </v-dialog>
</template>

<script>
import NerveButton from './NerveButton.vue';

export default {
  name: 'VmVideoOptions',
  components: {
    NerveButton,
  },
  props: {
    inputArray: {
      type: Array,
      default: () => [{}],
    },
    isVideoOptionEnabled: {
      type: Boolean,
      default: false,
    },
    isDialogOpen: {
      type: Boolean,
      default: false,
    },
    isGridDisplay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      array: [],
      highestCount: 0,
    };
  },
  computed: {
    dialogState: {
      get() {
        if (!this.isDialogOpen) {
          this.initSquence();
          this.arrayOrdering();
        }
        return this.isDialogOpen;
      },
      set() {
        this.close();
      },
    },
  },

  methods: {
    isElementOnTopOfTheList(item) {
      return this.array.findIndex((arrayElement) => arrayElement.deviceId === item.deviceId) === 0;
    },
    isElementOnBottomOfTheList(item) {
      const tempArray = this.array.filter((el) => el.display);
      return tempArray.findIndex((arrayElement) => arrayElement.deviceId === item.deviceId) === tempArray.length - 1;
    },
    /**
     * Close the VM Video Option Dialog
     */
    async close() {
      this.$emit('close');
    },
    /**
     * On clicking the save button the modified data is sent to the OVDM via a POST request
     */
    async save() {
      this.$emit('save', this.array);
      this.$emit('close');
    },
    /**
     * Checking or unchecking the checkbox, and preforming the logic of sorted
     * order the list of VMs
     */
    enablingCheckbox(item) {
      if (item.display) {
        this.highestCount = this.numberOfEnabled();
        item.sequence = this.highestCount;
        this.arrayOrdering();
        return;
      }
      if (!this.isDisabled(item)) {
        this.highestCount = this.numberOfEnabled();
        // eslint-disable-next-line no-restricted-syntax
        for (const element of this.array) {
          if (element.sequence > item.sequence) {
            element.sequence -= 1;
          }
        }
        this.arrayOrdering();
      }
    },
    numberOfEnabled() {
      return this.array.filter((item) => item.display).length;
    },
    /**
     * Initial setup of the sequence, countig how many VMs are enabled
     */
    async initSquence() {
      this.array = this.inputArray.map((item) => ({ ...item }));
      let numberOfEnabled = 0;
      // eslint-disable-next-line no-restricted-syntax
      for (const item of this.array) {
        if (item.display) {
          numberOfEnabled += 1;
        }
      }
      this.highestCount = numberOfEnabled;
      return true;
    },
    /**
     * Increment the sequence number of the VM
     * After every increment, the sorting of the array is called because
     * the list of VMs has to be orderd for a better view
     */
    increment(item) {
      if (item.sequence >= 1 && item.sequence < this.highestCount) {
        const nextItem = this.array.find((el) => el.sequence === item.sequence + 1);
        // eslint-disable-next-line no-plusplus
        nextItem.sequence--;
        // eslint-disable-next-line no-plusplus
        item.sequence++;
      }
      this.arrayOrdering();
    },
    /**
     * Decrement the sequence number of the VM
     * After every decrement, the sorting of the array is called because
     * the list of VMs has to be orderd for a better view
     */
    decrement(item) {
      if (item.sequence > 1 && item.sequence <= this.highestCount) {
        const nextItem = this.array.find((el) => el.sequence === item.sequence - 1);
        // eslint-disable-next-line no-plusplus
        nextItem.sequence++;
        // eslint-disable-next-line no-plusplus
        item.sequence--;
      }
      this.arrayOrdering();
    },
    /**
     * Ordering the array so that first the enabled VMs are at the top and in a order (1,2 .. n)
     * and after the enabled, all the other (disabled) VMs are displayed
     */
    arrayOrdering() {
      // every item that is not diplayed, his sequence number is set to 0
      // eslint-disable-next-line no-restricted-syntax
      for (const item of this.array) {
        if (item.display === false) {
          item.sequence = 0;
        }
      }
      const arrayTemp = this.array;
      const undefArray = arrayTemp.filter((item) => item.display === false);
      const defArray = arrayTemp.filter((item) => item.display === true);
      defArray.sort((a, b) => (a.sequence > b.sequence && a.sequence !== 0 && b.sequence !== 0 ? 1 : -1));
      this.array = defArray.concat(undefArray);
    },

    isDisabled(item) {
      // For the grid display option, only 4 virtual machines can be selected to the max
      // And others will be disabled
      return this.isGridDisplay && !item.display && this.numberOfEnabled() === 4;
    },

    videoDisplayModeMessage() {
      if (this.isVideoOptionEnabled) {
        return this.$t('workloadManagement.vmVideoOutputInfo');
      }
      if (this.isGridDisplay) {
        return this.$t('workloadManagement.vmGridDisplayInfo');
      }
      return '';
    },
  },
};
</script>
<style scoped lang="scss">
.primary-tttech-background-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}
.tertiary-tttech-background-btn {
  background-image: linear-gradient(to bottom, var(--v-tertiary-lighten4), var(--v-tertiary-base));
}
.vm-title {
  > h1 {
    font-size: 25px !important;
    font-weight: 800 !important;
    margin-bottom: 8px !important;
    margin-left: 0px !important;
    color: var(--v-teriary-base);
  }

  > .v-divider {
    border-top-width: 3px !important;
    border-top-color: var(--v-primary-base);
    width: 41px;
  }
}
.btn-appearance {
  @extend .primary-tttech-background-btn;
  width: 200px !important;
  height: 40px !important;
  text-transform: none;
  color: var(--v-secondary-base) !important;
  font-weight: bold !important;
  font-size: 16px;
  line-height: 1px;
}

.btns {
  @extend .btn-appearance;
  margin-top: 76px;
  margin-bottom: 40px;
  margin-left: 40%;
  &.cancel {
    @extend .tertiary-tttech-background-btn;
  }
}

.btns[disabled] {
  background-image: none;
  background-color: #e9e9e9;
  color: #ffffff;
}
.dialog_scroll {
  overflow-y: hidden;
  overflow-x: hidden;
}
.checkbox_display {
  text-align: center;
}
.item_display {
  margin: auto;
  // padding-top: 7px;
}
.number-text-field {
  width: 60px;
  margin: auto;
}
.right-icon {
  margin-left: 3%;
  height: 130px;
}
.list-style {
  height: 260px;
  overflow-y: auto;
  overflow-x: hidden;
}
.text-style {
  text-align: center;
}
.sequence-style {
  position: relative;
  left: 110px;
}
.page-wrapper-dialog {
  padding: 30px 30px 30px 30px;
}
.v-card-margin-left {
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
}
.name-style {
  text-align: center;
  margin-top: 15px;
}
.divider-width {
  max-width: 750px;
  border-top: 1px solid #eee;
  margin-right: 1px !important;
  margin-bottom: 1px;
}
</style>
